import {
  IMPACT_REPORT_CERTIFICATION_BADGES,
  IMPACT_REPORT_FALLBACK_IMAGE_SRC,
  IMPACT_REPORT_GFG_MARK_SRC,
  IMPACT_REPORT_MASTHEAD_LOCKUP_SRC,
} from "@/constants/impactReport";
import {
  IMPACT_REPORT_DOCUMENT_CSS_CONTENT_TYPE,
  IMPACT_REPORT_DOCUMENT_CSS_SRC,
  IMPACT_REPORT_DOCUMENT_FONT_FACES,
  IMPACT_REPORT_DOCUMENT_THEME_AT_RULE,
  IMPACT_REPORT_DOCUMENT_TOKENS_CSS_SRC,
} from "@/constants/impactReportDocument";
import type {
  ImpactReportBrandAssetDataUris,
  ImpactReportDocumentStyles,
  ImpactReportFontDataUris,
} from "@/interfaces/impactReportAssets";

/**
 * Thrown by the resolvers in this file when exactly one asset could not be turned into a data URI
 * (or, for a stylesheet, into CSS text). Named for WHICH asset and its source, on top of the underlying cause, so a failure
 * surfaces as a specific, attributable error rather than the document silently rendering with a
 * missing logo — the failure mode this module exists to prevent.
 */
export class ImpactReportAssetResolutionError extends Error {
  readonly assetLabel: string;
  readonly src: string;

  constructor(assetLabel: string, src: string, cause: unknown) {
    super(
      `Impact report PDF asset "${assetLabel}" (${src}) could not be resolved: ${
        cause instanceof Error ? cause.message : String(cause)
      }`,
    );
    this.name = "ImpactReportAssetResolutionError";
    this.assetLabel = assetLabel;
    this.src = src;
  }
}

/**
 * Fetches `src` and reads its body into a base64 `data:` URI.
 *
 * DESIGN TRADE-OFF (2026-08-31 decision — see `specs/change_client-impact-report-pdf-download.md`
 * "Images: two classes, and only one of them is Crunchy's"): resolves by FETCHING each asset at
 * call time rather than vendoring a second, pre-encoded copy into `public/`. That keeps exactly
 * one copy of each asset in existence — the CDN/R2 original every other surface in the app already
 * links to — with no base64 blob committed to the repo and nothing that can drift between "the
 * logo the app shows" and "the logo the PDF embeds". The cost, accepted deliberately: this makes
 * asset resolution I/O with its own failure mode (host unreachable, 404, a CORS-blocked fetch),
 * which is exactly why every call site must let a rejection propagate rather than swallow it into
 * a blank `<img>`. It is also why this is safe ONLY for assets whose bytes at a given URL do not
 * change — true of the five small brand logos this module resolves, never true in general and
 * never applied to `cause.image.url`, which stays a remote absolute URL by design.
 *
 * The masthead lockup is the one asset on the other side of that trade-off: its R2 host refuses
 * every CORS read, so it is fetched from a vendored same-origin copy — see
 * `IMPACT_REPORT_MASTHEAD_LOCKUP_SRC`. Pointing it back at `GFG_LOGO` for consistency fails every
 * download.
 */
async function fetchAsDataUri(
  src: string,
  assetLabel: string,
  signal?: AbortSignal,
): Promise<string> {
  let response: Response;
  try {
    response = await fetch(src, { signal });
  } catch (cause) {
    throw new ImpactReportAssetResolutionError(assetLabel, src, cause);
  }
  if (!response.ok) {
    throw new ImpactReportAssetResolutionError(
      assetLabel,
      src,
      new Error(`unexpected HTTP status ${response.status}`),
    );
  }

  // A body read can reject after the headers arrived (abort, dropped connection); unwrapped, that
  // escapes with no asset named.
  let blob: Blob;
  try {
    blob = await response.blob();
  } catch (cause) {
    throw new ImpactReportAssetResolutionError(assetLabel, src, cause);
  }

  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new ImpactReportAssetResolutionError(assetLabel, src, reader.error));
    reader.readAsDataURL(blob);
  });
}

/**
 * Resolves the five small brand assets/logos the PDF serialiser embeds as data URIs — see
 * `ImpactReportBrandAssetDataUris` for which five, and this file's own header comment for why
 * these five, and only these five, are inlined rather than left as remote URLs.
 *
 * Runs entirely in the browser (`fetch`, `FileReader`, `Promise.all`): the serialiser that calls
 * this is UI-side, so there is no Bun/Node filesystem available at the point this runs.
 *
 * `IMPACT_REPORT_GFG_MARK_SRC` is read as-is and MUST NOT be edited here or anywhere else — it
 * already names a real Shopify CDN asset (see that constant's own docblock for the history of the
 * defect this would otherwise reintroduce).
 *
 * A rejection from any one asset fails the whole resolution rather than silently omitting that
 * asset — the failure this exists to make visible and specific, per
 * `ImpactReportAssetResolutionError`.
 */
export async function resolveImpactReportBrandAssets(
  signal?: AbortSignal,
): Promise<ImpactReportBrandAssetDataUris> {
  const [gfgMark, masthead, fallbackBackdrop, ...certificationBadges] = await Promise.all([
    fetchAsDataUri(IMPACT_REPORT_GFG_MARK_SRC, "closing-page GfG mark", signal),
    fetchAsDataUri(IMPACT_REPORT_MASTHEAD_LOCKUP_SRC, "masthead lockup", signal),
    fetchAsDataUri(IMPACT_REPORT_FALLBACK_IMAGE_SRC, "bundled fallback backdrop", signal),
    ...IMPACT_REPORT_CERTIFICATION_BADGES.map((badge) =>
      fetchAsDataUri(badge.src, `certification badge (${badge.alt})`, signal),
    ),
  ]);

  return { gfgMark, masthead, fallbackBackdrop, certificationBadges };
}

/**
 * Reads the four vendored `woff2` subsets into base64 `data:` URIs, keyed by the face list in
 * `constants/impactReportDocument.ts`.
 *
 * Fetched from this app's own origin rather than imported: they live in `public/fonts`, which the
 * dev server and production both route, and no bundler loader hands a font back as base64.
 *
 * A rejection from any one face fails the whole resolution, exactly as the brand assets do: a
 * document that silently dropped one face prints that weight in the platform's default sans, which
 * no review of the PDF's content would catch.
 */
export async function resolveImpactReportDocumentFonts(
  signal?: AbortSignal,
): Promise<ImpactReportFontDataUris> {
  const entries = await Promise.all(
    IMPACT_REPORT_DOCUMENT_FONT_FACES.map(
      async (face) =>
        [
          face.key,
          await fetchAsDataUri(face.path, `${face.family} ${face.weight}`, signal),
        ] as const,
    ),
  );

  // `ImpactReportFontDataUris` is a Record over the key union derived from this same list, so the
  // entries above are exactly its keys.
  return Object.fromEntries(entries) as ImpactReportFontDataUris;
}

/**
 * Reads one served stylesheet as plain text.
 *
 * The Content-Type check is what catches a miss that still answered 200: a host without a
 * `/styles/*` route hands back the SPA's `index.html`, which would otherwise be embedded as CSS
 * and print the report unstyled with nothing logged.
 */
async function fetchAsCssText(
  src: string,
  assetLabel: string,
  signal?: AbortSignal,
): Promise<string> {
  let response: Response;
  try {
    response = await fetch(src, { signal });
  } catch (cause) {
    throw new ImpactReportAssetResolutionError(assetLabel, src, cause);
  }
  if (!response.ok) {
    throw new ImpactReportAssetResolutionError(
      assetLabel,
      src,
      new Error(`unexpected HTTP status ${response.status}`),
    );
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().startsWith(IMPACT_REPORT_DOCUMENT_CSS_CONTENT_TYPE)) {
    throw new ImpactReportAssetResolutionError(
      assetLabel,
      src,
      new Error(`unexpected content type "${contentType || "(none)"}"`),
    );
  }

  let text: string;
  try {
    text = await response.text();
  } catch (cause) {
    throw new ImpactReportAssetResolutionError(assetLabel, src, cause);
  }

  // Empty means the copy step wrote nothing, not a document with no rules.
  if (text.trim() === "") {
    throw new ImpactReportAssetResolutionError(assetLabel, src, new Error("stylesheet is empty"));
  }
  return text;
}

/**
 * Rewrites the tokens file's Tailwind `@theme { … }` wrapper to a plain `:root { … }` — the ONE
 * transformation applied to either stylesheet; values are never touched. A converter running no
 * Tailwind build ignores `@theme` wholesale, so without this every `var()` in the document would
 * resolve to nothing.
 */
function toRootBlock(source: string): string {
  if (!IMPACT_REPORT_DOCUMENT_THEME_AT_RULE.test(source)) {
    console.error(
      "[impact-report] the document tokens stylesheet has no top-level `@theme { … }` wrapper to rewrite to `:root`; the serialised PDF will resolve none of its design tokens.",
    );
    return source;
  }
  return source.replace(IMPACT_REPORT_DOCUMENT_THEME_AT_RULE, "$1:root$2");
}

/**
 * Both document stylesheets, fetched in parallel from the copies served under `/styles/*`. See
 * `ImpactReportDocumentStyles` for why they are fetched rather than imported as text.
 */
export async function resolveImpactReportDocumentStyles(
  signal?: AbortSignal,
): Promise<ImpactReportDocumentStyles> {
  const [tokensSource, documentCss] = await Promise.all([
    fetchAsCssText(IMPACT_REPORT_DOCUMENT_TOKENS_CSS_SRC, "document design tokens", signal),
    fetchAsCssText(IMPACT_REPORT_DOCUMENT_CSS_SRC, "document stylesheet", signal),
  ]);
  return { tokensCss: toRootBlock(tokensSource), documentCss };
}
